import React from 'react';
import {
  Card,
  CardTitle,
  Col,
  Container,
  FormFeedback,
  Input,
  Row,
  Table,
} from 'reactstrap';
import { get } from 'lodash';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useFormik } from 'formik';
import { useLocation, useHistory } from 'react-router-dom';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppCheckboxList from 'components/App/AppCheckboxList';
import AppFlatPickInput from 'components/App/FlatPickInput';
import AppMeta from 'components/App/Meta';
import FormField from 'components/AddLocation/FormField';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import { LOCATIONS, LOGIN_TYPE, ROOM_TYPE, storageKey, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import {
  checkHadError,
} from 'utils';
import { updateNumberAvailableRoom } from 'utils/validate';

import styles from './AmountRoom.module.scss';

const breadcrumbList = [
  {
    name: 'Giá và tình trạng phòng trống',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Cập nhật số lượng phòng',
    isActive: true,
    link: '',
  },
];

const newDate = new Date().setHours(0, 0, 0, 0);
const aWeekAgo = new Date(moment(newDate).add(7, 'days'));

const optionsFlatpickr = {
  minDate: 'today',
};

function AmountRoom(props) {
  const {
    isLoadingUpdateAvailableRoom,

    roomOfPlaceList,

    actionGetListRoomsOfPlace,
    actionUpdateAvailableRoom,
  } = props;
  const location = useLocation();
  const history = useHistory();
  const placeId = new URLSearchParams(location.search).get('place');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const validation = useFormik({
    initialValues: {
      startDate: newDate,
      endDate: aWeekAgo,
      weekDays: DATES_OF_WEEK,
      rooms: [],
    },

    validationSchema: Yup.object(updateNumberAvailableRoom),

    onSubmit: (values) => {
      const data = {
        startDate: new Date(values.startDate),
        endDate: new Date(values.endDate.setUTCHours(23, 59, 59)),
        weekDays: values.weekDays
          .filter((item) => item.isChecked)
          .map((item) => +item.value),
        rooms: values.rooms
          .filter((item) => item.isChecked)
          .map((item) => ({
            roomId: item._id,
            availableRooms: +item.availableRooms,
          })),
      };

      actionUpdateAvailableRoom({
        placeId,
        data,
      });
    },
  });

  const onChangeStartDate = React.useCallback((date) => {
    if (date.length === 0) {
      validation.setValues((prevState) => ({
        ...prevState,
        startDate: '',
      }));
    } else {
      const checkDateEnd = date[0] > moment(validation.values.endDate).subtract(7, 'days');
      if (checkDateEnd) {
        validation.setValues((prevState) => ({
          ...prevState,
          endDate: '',
        }));
      }

      validation.setValues((prevState) => ({
        ...prevState,
        startDate: date[0],
      }));
    }
  }, [validation.values.endDate]);

  const onChangeEndDate = React.useCallback((date) => {
    validation.setValues((prevState) => ({
      ...prevState,
      endDate: date.length > 0 ? date[0] : '',
    }));
  }, []);

  const onToggleCheckedDateInWeek = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      weekDays: values.weekDays.map((item) => {
        if (item.value === +e.target.value) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, []);

  const onToggleSelectAllDate = React.useCallback((e) => {
    validation.setFieldValue('weekDays', validation.values.weekDays.map((item) => ({
      ...item,
      isChecked: +e.target.value === 1,
    })));
  }, [validation.values]);

  const onToggleCheckedRoom = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      rooms: values.rooms.map((item) => {
        if (item._id === e.target.value) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, [validation]);

  const onToggleCheckedAllRooms = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      rooms: values.rooms.map((item) => ({
        ...item,
        isChecked: e.target.checked,
      })),
    }));
  }, []);

  const onChangeRoomAvailableRooms = React.useCallback((roomId) => (e) => {
    validation.setValues((values) => ({
      ...values,
      rooms: values.rooms.map((item) => {
        if (item._id === roomId) {
          return {
            ...item,
            availableRooms: e.target.value,
          };
        }
        return item;
      }),
    }));
  }, []);

  const onCancelUpdateRoom = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`);
  }, []);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    validation.setValues((values) => ({
      ...values,
      rooms: roomOfPlaceList.map((room) => ({
        _id: room._id,
        name: room.name,
        roomName: room.roomName,
        quantity: room.quantity,
        availableRooms: 1,
        isChecked: false,
      })),
    }));
  }, [roomOfPlaceList]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta
        name="Cập nhật số lượng phòng"
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="Cập nhật số lượng phòng"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn thay đổi ngày nào?
          </CardTitle>

          <Row>
            <Col md="6" className="mb-24">
              <FormField label="Từ ngày">
                <AppFlatPickInput
                  name="startDate"
                  validation={validation}
                  onChange={onChangeStartDate}
                  options={optionsFlatpickr}
                />
              </FormField>
            </Col>

            <Col md="6" className="mb-24">
              <FormField label="Đến ngày">
                <AppFlatPickInput
                  name="endDate"
                  validation={validation}
                  onChange={onChangeEndDate}
                  options={{
                    minDate: moment(validation.values.startDate || newDate).toDate(),
                  }}
                />
              </FormField>
            </Col>
          </Row>

          <AppCheckboxList
            name="weekDays"
            list={validation.values.weekDays}
            validation={validation}
            onChecked={onToggleCheckedDateInWeek}
            onToggleCheckedAll={onToggleSelectAllDate}
          />
        </Card>

        <Card className="Section mb-4">
          <CardTitle className="Section__Title">
            Quý vị muốn thay đổi số lượng phòng nào?
          </CardTitle>
          {checkHadError(validation, 'rooms', '') && (
            <FormFeedback type="invalid" className="d-block mb-10">
              {get(validation.errors, 'rooms', '')}
            </FormFeedback>
          )}

          <Table className="table table-striped mb-0">
            <thead>
              <tr className="TableHeader">
                <th>
                  <Input
                    type="checkbox"
                    checked={validation.values.rooms.every((item) => item.isChecked)}
                    className="Checkbox__Input"
                    onChange={onToggleCheckedAllRooms}
                  />
                </th>
                <th>Phòng</th>
                <th>Số lượng phòng</th>
              </tr>
            </thead>
            <tbody>
              {validation.values.rooms.map((item) => (
                <tr key={item._id}>
                  <th className="align-middle">
                    <Input
                      id={item._id}
                      type="checkbox"
                      checked={item.isChecked}
                      className="Checkbox__Input"
                      value={item._id}
                      onChange={onToggleCheckedRoom}
                    />
                  </th>

                  <td className="align-middle">
                    <label htmlFor={item._id} className="roomName">
                      <span>{item.name || ROOM_TYPE[item.roomName]?.label}</span>
                    </label>
                  </td>

                  <td>
                    <Input
                      className="Section__Input__Number"
                      type="number"
                      step={1}
                      name="rooms"
                      value={item.availableRooms}
                      onChange={onChangeRoomAvailableRooms(item._id)}
                      invalid={+item.availableRooms > item.quantity && validation.errors.rooms}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <AppButton color={themeColor.SECONDARY} onClick={onCancelUpdateRoom}>
              Hủy
            </AppButton>

            <AppButton
              loading={isLoadingUpdateAvailableRoom}
              onClick={validation.handleSubmit}
            >
              Cập nhật
            </AppButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

AmountRoom.propTypes = {
  isLoadingUpdateAvailableRoom: PropTypes.bool.isRequired,
  roomOfPlaceList: PropTypes.instanceOf(Array),

  actionGetListRoomsOfPlace: PropTypes.func,
  actionUpdateAvailableRoom: PropTypes.func,
};

AmountRoom.defaultProps = {
  roomOfPlaceList: INITIAL_VALUE.ARRAY,

  actionGetListRoomsOfPlace: INITIAL_VALUE.FUNCTION,
  actionUpdateAvailableRoom: INITIAL_VALUE.FUNCTION,
};

export default AmountRoom;
